/**
 * Gateway approvals on file (11-compliance/02 §4.2–4.3), under the flag inventory.
 *
 * A flag marked as needing the gateway's approval cannot leave `off` until an approval naming it
 * is filed here — the server refuses the flip, this screen only explains it. Each record keeps the
 * date, the scope approved, the exact description submitted, the review date and the letter, which
 * is uploaded first (private, scanned) and attached by id. A filed record is never edited; a wider
 * or renewed approval is a new one. Superadmin only, so nobody else sees the section at all.
 */
import { useState } from 'react';
import { Note } from '../components/primitives';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { FileField, Input } from '../components/forms';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { can } from '../lib/rbac';
import {
  LETTER_TYPES,
  useGatewayApprovals,
  useRecordApproval,
  useUploadLetter,
} from '../lib/api/hooks/gatewayApprovals';

const EMPTY = {
  gateway: 'razorpay',
  flag_key: '',
  approved_on: '',
  scope_approved: '',
  description_submitted: '',
  review_on: '',
};

export function GatewayApprovals() {
  const { scope, toast } = useConsole();
  const superadmin = can(scope, 'superadmin');
  const list = useGatewayApprovals(superadmin);
  const upload = useUploadLetter();
  const record = useRecordApproval();
  const [draft, setDraft] = useState(EMPTY);
  const [letter, setLetter] = useState<{ id: number; name: string } | null>(null);
  const [confirming, setConfirming] = useState(false);

  if (!superadmin) return null;

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });
  const items = list.data?.items ?? [];
  const flag = draft.flag_key.trim();
  const ready =
    letter !== null &&
    [draft.gateway, draft.approved_on, draft.scope_approved, draft.description_submitted].every(
      (v) => v.trim() !== '',
    );

  return (
    <Section title="Gateway approvals on file" note="A marked flag stays off until one names it">
      <Note>
        A flag that changes what money is collected for is marked as needing the gateway&apos;s
        written approval. It cannot be turned on until an approval naming it is filed here, with
        the letter attached.
      </Note>
      {list.isPending && <LoadingState message="Reading the approvals on file…" />}
      {list.isError && (
        <ErrorState title="Could not read the approvals" cause={errorMessage(list.error)} />
      )}
      {list.isSuccess && items.length === 0 && (
        <EmptyState
          title="No approvals on file"
          body="Every flag marked as needing one stays off until its approval is filed."
          inline
        />
      )}
      {items.length > 0 && (
        <DataTable
          columns={[
            { key: 'approved', label: 'Approved' },
            { key: 'gateway', label: 'Gateway' },
            { key: 'flag', label: 'Unlocks', mono: true },
            { key: 'scope', label: 'Scope approved' },
            { key: 'review', label: 'Review' },
            { key: 'letter', label: 'Letter' },
          ]}
          rows={items.map((r) => ({
            id: String(r.id),
            approved: r.approved_on,
            gateway: r.gateway,
            flag: r.flag_key ?? 'The product as a whole',
            scope: r.scope_approved,
            review: r.review_on ?? 'No review date',
            letter: (
              <a href={r.letter_url} target="_blank" rel="noreferrer">
                Open letter
              </a>
            ),
          }))}
        />
      )}

      <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap', marginTop: 'var(--sp-md)' }}>
        <FileField
          label="Approval letter"
          inputLabel="Gateway approval letter"
          accept={LETTER_TYPES.join(',')}
          pending={upload.isPending}
          audited
          onFile={(file) =>
            upload.mutate(file, {
              onSuccess: (confirmed) => setLetter({ id: confirmed.id, name: file.name }),
              onError: fail,
            })
          }
        />
        {letter && <span>Attached: {letter.name}</span>}
        <Input label="Gateway" value={draft.gateway} onChange={(v) => setDraft({ ...draft, gateway: v })} />
        <Input
          label="Flag it unlocks"
          value={draft.flag_key}
          onChange={(v) => setDraft({ ...draft, flag_key: v })}
          helper="Blank for an approval of the product as a whole"
        />
        <Input
          label="Approved on"
          value={draft.approved_on}
          onChange={(v) => setDraft({ ...draft, approved_on: v })}
          placeholder="2026-10-20"
        />
        <Input
          label="Gateway review on"
          value={draft.review_on}
          onChange={(v) => setDraft({ ...draft, review_on: v })}
          placeholder="2027-10-20"
        />
        <Input
          label="Scope approved"
          value={draft.scope_approved}
          onChange={(v) => setDraft({ ...draft, scope_approved: v })}
        />
        <Input
          label="Description submitted, exactly as sent"
          multiline
          value={draft.description_submitted}
          onChange={(v) => setDraft({ ...draft, description_submitted: v })}
        />
        <ScopeAction scope={scope} need="superadmin" size="sm" disabled={!ready} onClick={() => setConfirming(true)}>
          File approval
        </ScopeAction>
      </div>
      <ConfirmAction
        open={confirming}
        title={`File the ${draft.gateway} approval?`}
        phrase="FILE"
        tone="warn"
        consequences={[
          flag
            ? `${flag} can then be turned on by whoever owns it.`
            : 'It names no flag, so it unlocks nothing: it records the approval of the product.',
          'A filed approval is never edited. A wider or renewed approval is a new record.',
        ]}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          record.mutate(
            {
              ...draft,
              flag_key: flag || null,
              review_on: draft.review_on || null,
              media_object_id: letter!.id,
            },
            {
              onSuccess: () => {
                setDraft(EMPTY);
                setLetter(null);
                toast({ tone: 'success', message: 'Approval filed' });
              },
              onError: fail,
            },
          );
        }}
        confirmLabel="File approval"
      />
    </Section>
  );
}
